import React, { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Trash2, Mail, Phone, MapPin, Calendar, RefreshCw } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lbj_admin_token";

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchEntries = async (t = token) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/waitlist`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setEntries(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setToken("");
        localStorage.removeItem(TOKEN_KEY);
        setError("Session expired. Please log in again.");
      } else {
        setError("Unable to load submissions. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchEntries(token);
    // eslint-disable-next-line
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem(TOKEN_KEY, t);
      setPassword("");
      fetchEntries(t);
    } catch (err) {
      setError("Incorrect password.");
    }
  };

  const handleLogout = () => {
    setToken("");
    localStorage.removeItem(TOKEN_KEY);
    setEntries([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, { headers: authHeaders });
      setEntries((es) => es.filter((e) => e.id !== id));
    } catch (err) {
      alert("Unable to delete.");
    }
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso || "";
    }
  };

  if (!token) {
    return (
      <>
        <Header />
        <div className="admin-login-wrap">
          <div className="admin-login-card">
            <div className="section-line" />
            <h1 className="page-title" style={{ marginBottom: 30 }}>Atelier Access</h1>
            <p style={{ fontSize: 14, color: "#6b6258", marginBottom: 30, fontWeight: 300 }}>
              Please enter the atelier password to view Register Your Interest submissions.
            </p>
            {error && <div className="form-error">{error}</div>}
            <form onSubmit={handleLogin}>
              <div className="form-field">
                <label>Password</label>
                <input
                  type="password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <button type="submit" className="btn-primary" style={{ marginTop: 20, width: "100%" }}>
                Enter
              </button>
            </form>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="admin-wrap">
        <div className="admin-header">
          <div>
            <div className="section-line" />
            <h1 className="page-title" style={{ marginBottom: 8 }}>Register Your Interest</h1>
            <p style={{ fontSize: 13, color: "#6b6258", margin: 0, fontWeight: 300 }}>
              {entries.length} submission{entries.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="admin-actions">
            <button className="icon-btn" onClick={() => fetchEntries()} title="Refresh">
              <RefreshCw size={16} strokeWidth={1.4} />
              <span>Refresh</span>
            </button>
            <button className="icon-btn" onClick={handleLogout} title="Log out">
              <LogOut size={16} strokeWidth={1.4} />
              <span>Log out</span>
            </button>
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}
        {loading && <p style={{ color: "#6b6258", fontSize: 13 }}>Loading…</p>}

        {!loading && entries.length === 0 && (
          <div className="empty-state">
            <p>No submissions yet.</p>
          </div>
        )}

        <div className="admin-list">
          {entries.map((e) => {
            const isOpen = expanded === e.id;
            return (
              <article key={e.id} className={`admin-card ${isOpen ? "open" : ""}`}>
                <div className="admin-card-head" onClick={() => setExpanded(isOpen ? null : e.id)}>
                  <div>
                    <h3 className="admin-name">{e.firstName} {e.lastName}</h3>
                    <div className="admin-meta">
                      <span><Mail size={12} /> {e.email}</span>
                      <span><Phone size={12} /> {e.mobile}</span>
                      <span><MapPin size={12} /> {e.location}</span>
                      <span><Calendar size={12} /> {fmt(e.submittedAt)}</span>
                    </div>
                  </div>
                  <div className="admin-tags">
                    <span className="tag">{e.occasion}</span>
                    <span className="tag tag-gold">{e.investmentRange}</span>
                  </div>
                </div>

                {isOpen && (
                  <div className="admin-card-body">
                    <Row label="Expected delivery" value={e.deliveryDate} />
                    <Row label="Size / height / weight" value={e.measurements} />
                    <Row label="Prior commission" value={e.priorCommission || "—"} />
                    <Row label="Story image (filename)" value={e.imageName || "—"} />
                    {e.imageData && (
                      <div className="admin-block">
                        <div className="admin-block-label">Reference image</div>
                        <a href={e.imageData} target="_blank" rel="noreferrer" className="admin-image-wrap">
                          <img src={e.imageData} alt={e.imageName || "Reference"} className="admin-image" />
                        </a>
                      </div>
                    )}
                    <div className="admin-block">
                      <div className="admin-block-label">Your story</div>
                      <p>{e.story}</p>
                    </div>
                    <div className="admin-block">
                      <div className="admin-block-label">What draws you to Label Binduu Jain</div>
                      <p>{e.draws}</p>
                    </div>
                    <div className="admin-card-footer">
                      <a className="icon-btn" href={`mailto:${e.email}`}>
                        <Mail size={14} strokeWidth={1.4} /> <span>Reply</span>
                      </a>
                      <button className="icon-btn danger" onClick={() => handleDelete(e.id)}>
                        <Trash2 size={14} strokeWidth={1.4} /> <span>Delete</span>
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
      <Footer />
    </>
  );
};

const Row = ({ label, value }) => (
  <div className="admin-row">
    <span className="admin-row-label">{label}</span>
    <span className="admin-row-value">{value}</span>
  </div>
);

export default Admin;
